/**
 * Real page elements opt in to being highlighted by spreading `spotlightTarget(id)` onto an
 * existing View/TextInput/Pressable. It only adds a `data-spotlight-target` attribute, so the page
 * layout is never changed to support the tutorial.
 */
export const SPOTLIGHT_TARGETS = {
	PROFILE_PAGE: "profile-page",
	PROFILE_FIRST_NAME: "profile-first-name",
	PROFILE_LAST_NAME: "profile-last-name",
	PROFILE_EMAIL: "profile-email",
	PROFILE_PASSWORD: "profile-password",
	PROFILE_ACCOUNT_TYPE: "profile-account-type",
	PROFILE_ACCOUNT_TYPE_CONFIRM_MODAL: "profile-account-type-confirm-modal",
	PROFILE_ACCOUNT_TYPE_CONFIRM: "profile-account-type-confirm",
	PROFILE_ACCOUNT_TYPE_SUCCESS_MODAL: "profile-account-type-success-modal",
	PROFILE_REFRESH_NOW: "profile-refresh-now",
	PROFILE_ORGANIZATION: "profile-organization",
	PROFILE_DEGREE: "profile-degree",
	PROFILE_INSTITUTION_CODE: "profile-institution-code",
	PROFILE_GRADUATION_DATE: "profile-graduation-date",
	PROFILE_SHIPPING_ADDRESS: "profile-shipping-address",
	PROFILE_SHIPPING_CITY: "profile-shipping-city",
	PROFILE_SHIPPING_STATE: "profile-shipping-state",
	PROFILE_SHIPPING_ZIP: "profile-shipping-zip",
	PROFILE_SAVE: "profile-save",
	HUB_PAGE: "hub-page",
	HUB_MY_PROFILE: "hub-my-profile",
	HUB_MY_ANNOUNCEMENT: "hub-my-announcement",
	HUB_REGISTRY: "hub-registry",
	HUB_DASHBOARD: "hub-dashboard",
	HEADER_GO_HOME: "header-go-home",
	HEADER_LOGOUT: "header-logout",
	HEADER_FRIENDS: "header-friends",
	HEADER_SETTINGS: "header-settings",
	FRIENDS_MODAL: "friends-modal",
	FRIENDS_PRIVACY: "friends-privacy",
	FRIENDS_SEARCH: "friends-search",
	FRIENDS_TABS: "friends-tabs",
	FRIENDS_CLOSE: "friends-close",
	SETTINGS_MODAL: "settings-modal",
	SETTINGS_PRIVACY: "settings-privacy",
	SETTINGS_SHOW_TUTORIALS: "settings-show-tutorials",
	SETTINGS_CLOSE: "settings-close",
	ANNOUNCEMENT_PAGE: "announcement-page",
	ANNOUNCEMENT_VIEW_AS_VISITOR: "announcement-view-as-visitor",
	ANNOUNCEMENT_EDIT_PAGE_SETTINGS: "announcement-edit-page-settings",
	PAGE_SETTINGS_MODAL: "page-settings-modal",
	PAGE_SETTINGS_BACKGROUND: "page-settings-background",
	PAGE_SETTINGS_TEXT_COLOR: "page-settings-text-color",
	PAGE_SETTINGS_URL: "page-settings-url",
	PAGE_SETTINGS_BLUR: "page-settings-blur",
	PAGE_SETTINGS_ACTIONS: "page-settings-actions",
	ANNOUNCEMENT_GRADUATION_CARD: "announcement-graduation-card",
	ANNOUNCEMENT_YEARBOOK: "announcement-yearbook",
	ANNOUNCEMENT_FUND_TAB: "announcement-fund-tab",
	ANNOUNCEMENT_REGISTRY_TAB: "announcement-registry-tab",
	ANNOUNCEMENT_SHARE_TAB: "announcement-share-tab",
	ANNOUNCEMENT_FUND_SECTION: "announcement-fund-section",
	ANNOUNCEMENT_SHARE_SECTION: "announcement-share-section",
	WIDGET_PICKER: "widget-picker",
	WIDGET_ADD: "widget-add",
	WIDGET_CARD: "widget-card",
	GRADFUND_SETUP_MODAL: "gradfund-setup-modal",
	GRADFUND_SETUP_SKIP: "gradfund-setup-skip",
	REGISTRY_BROWSE_CATALOGUE: "registry-browse-catalogue",
	REGISTRY_STARTER_CHOICES: "registry-starter-choices",
	REGISTRY_STARTER_ITEMS: "registry-starter-items",
	REGISTRY_CATALOGUE_MODAL: "registry-catalogue-modal",
	REGISTRY_ADD_SUCCESS: "registry-add-success",
	REGISTRY_ITEMS_SECTION: "registry-items-section",
	REGISTRY_GOAL_TRACKER: "registry-goal-tracker",
	REGISTRY_GOAL_SET: "registry-goal-set",
	REGISTRY_GOAL_ACTIVATE: "registry-goal-activate",
	REGISTRY_YEARBOOK: "registry-yearbook",
	REGISTRY_SHARE: "registry-share",
	GOAL_EDITOR: "goal-editor",
};

export const widgetOptionTarget = (widgetType) => `widget-option-${widgetType}`;

export const SPOTLIGHT_TARGET_ATTRIBUTE = "data-spotlight-target";

export const spotlightTarget = (id) => ({ dataSet: { spotlightTarget: id } });

/** Marks the element only while `condition` holds (e.g. just the last card of a list). */
export const spotlightTargetWhen = (condition, id) => (condition ? spotlightTarget(id) : {});

const isRendered = (element) => {
	const rect = element.getBoundingClientRect();
	return rect.width > 0 && rect.height > 0;
};

const findRenderedTarget = (id) => {
	const matches = document.querySelectorAll(`[${SPOTLIGHT_TARGET_ATTRIBUTE}="${id}"]`);
	return Array.from(matches).find(isRendered) || null;
};

/**
 * Returns the first rendered element for `id` (the same id may exist in a hidden mobile/desktop variant).
 * `id` may be a list of alternatives; the first one that is on screen wins.
 */
export const findSpotlightTargetElement = (id) => {
	if (typeof document === "undefined" || !id) return null;
	const candidates = Array.isArray(id) ? id : [id];
	for (const candidate of candidates) {
		const element = findRenderedTarget(candidate);
		if (element) return element;
	}
	return null;
};
